import { IRestaurantWorkingTimes } from '@/shared/model/restaurant-working-times.model';
import { IDeliverySpec } from '@/shared/model/delivery-spec.model';
import { MutationTree } from 'vuex';
import { IRestaurant } from '@/shared/model/restaurant.model';
import { IUser } from '@/shared/model/user.model';
import { IDiscount } from '@/shared/model/discount.model';
import { IRating } from '@/shared/model/rating.model';
import { IMenuCategory } from '@/shared/model/menu-category.model';
import { IRestaurantCategory } from '@/shared/model/restaurant-category.model';
import { ICustomer } from '@/shared/model/customer.model';
import { IMarketer, Marketer } from '@/shared/model/marketer.model';
import { IRestaurantMenuStore } from './types';
import { Address, IAddress } from '@/shared/model/address.model';
import { IOrderDetail } from '@/shared/model/order/order-detail.model';
import { IOrderMenu } from "@/shared/model/order/order-menu.model";

export const mutations: MutationTree<IRestaurantMenuStore> = {
  setRestaurant(state, newRestaurant: IRestaurant): void {
    newRestaurant.marketer = newRestaurant.marketer ?? new Marketer();
    state.restaurant = newRestaurant;
    state.isDeliver = newRestaurant.deliver;
  },
  setId(state, newId: number): void {
    state.restaurant.id = newId;
  },
  setName(state, newName: string): void {
    state.restaurant.name = newName;
  },
  setLogoContentType(state, newLogoContentType: string): void {
    state.restaurant.logoContentType = newLogoContentType;
  },
  setLogo(state, newLogo: string): void {
    state.restaurant.logo = newLogo;
  },
  setImageContentType(state, newImageContentType: string): void {
    state.restaurant.imageContentType = newImageContentType;
  },
  setImage(state, newImage: string): void {
    state.restaurant.image = newImage;
  },
  setZipcode(state, newZipcode: string): void {
    state.restaurant.address.zipcode = newZipcode;
  },
  setStreet(state, newStreet: string): void {
    state.restaurant.address.street = newStreet;
  },
  setAddressAddition(state, newAddressAddition: string): void {
    state.restaurant.address.addressAddition = newAddressAddition;
  },
  setNumber(state, newNumber: string): void {
    state.restaurant.address.number = newNumber;
  },
  setCity(state, newCity: string): void {
    state.restaurant.address.city = newCity;
  },
  setPhone(state, newPhone: string): void {
    state.restaurant.address.phone = newPhone;
  },
  setRegisterNumber(state, newRegisterNumber: string): void {
    state.restaurant.registerNumber = newRegisterNumber;
  },
  setTaxIdentificationNumber(state, newTaxIdentificationNumber: string): void {
    state.restaurant.taxIdentificationNumber = newTaxIdentificationNumber;
  },
  setDeliver(state, newDeliver: boolean): void {
    state.restaurant.deliver = newDeliver;
  },
  setPickup(state, newPickup: boolean): void {
    state.restaurant.pickup = newPickup;
  },
  setBankAccountName(state, newBankAccountName: string): void {
    state.restaurant.bankAccountName = newBankAccountName;
  },
  setIbanNumber(state, newIbanNumber: string): void {
    state.restaurant.ibanNumber = newIbanNumber;
  },
  setEnabled(state, newEnabled: boolean): void {
    state.restaurant.enabled = newEnabled;
  },
  setStartingDate(state, newStartingDate: Date): void {
    state.restaurant.startingDate = newStartingDate;
  },
  setStartingDateString(state, newStartingDateString: string): void {
    state.restaurant.startingDateString = newStartingDateString;
  },
  setDomain(state, newDomain: boolean): void {
    state.restaurant.domain = newDomain;
  },
  setMenuCard(state, newMenuCard: string[]): void {
    state.restaurant.menuCard = newMenuCard;
  },
  setMenuCardPath(state, newMenuCardPath: string): void {
    state.restaurant.menuCardPath = newMenuCardPath;
  },
  setMenuCardContentType(state, newMenuCardContentType: string): void {
    state.restaurant.menuCardContentType = newMenuCardContentType;
  },
  setIdCard(state, newIdCard: string[]): void {
    state.restaurant.idCard = newIdCard;
  },
  setIdCardPath(state, newIdCardPath: string): void {
    state.restaurant.idCardPath = newIdCardPath;
  },
  setIdCardContentType(state, newIdCardContentType: string): void {
    state.restaurant.idCardContentType = newIdCardContentType;
  },
  setCocRegistration(state, newCocRegistration: string[]): void {
    state.restaurant.cocRegistration = newCocRegistration;
  },
  setCocRegistrationPath(state, newCocRegistrationPath: string): void {
    state.restaurant.cocRegistrationPath = newCocRegistrationPath;
  },
  setCocRegistrationContentType(state, newCocRegistrationContentType: string): void {
    state.restaurant.cocRegistrationContentType = newCocRegistrationContentType;
  },
  setBankCard(state, newBankCard: string[]): void {
    state.restaurant.bankCard = newBankCard;
  },
  setBankCardPath(state, newBankCardPath: string): void {
    state.restaurant.bankCardPath = newBankCardPath;
  },
  setBankCardContentType(state, newBankCardContentType: string): void {
    state.restaurant.bankCardContentType = newBankCardContentType;
  },
  setUser(state, newUser: IUser): void {
    state.restaurant.user = newUser;
  },
  setFoodDiscount(state, newFoodDiscount: IDiscount): void {
    state.restaurant.foodDiscount = newFoodDiscount;
  },
  setNonfoodDiscount(state, newNonfoodDiscount: IDiscount): void {
    state.restaurant.nonfoodDiscount = newNonfoodDiscount;
  },
  setRatings(state, newRatings: IRating[]): void {
    state.restaurant.ratings = newRatings;
  },
  setDeliverySpecs(state, newDeliverySpecs: IDeliverySpec[]): void {
    state.restaurant.deliverySpecs = newDeliverySpecs;
  },
  setRestaurantWorkingTimes(state, newRestaurantWorkingTimes: IRestaurantWorkingTimes[]): void {
    state.restaurant.restaurantWorkingTimes = newRestaurantWorkingTimes;
  },
  setMenuCategories(state, newMenuCategories: IMenuCategory[]): void {
    state.restaurant.menuCategories = newMenuCategories;
  },
  setRestaurantCategories(state, newRestaurantCategories: IRestaurantCategory[]): void {
    state.restaurant.restaurantCategories = newRestaurantCategories;
  },
  setCustomer(state, newCustomer: ICustomer): void {
    state.restaurant.customer = newCustomer;
  },
  setMarketer(state, newMarketer: IMarketer): void {
    state.restaurant.marketer = newMarketer;
  },
  setIsDeliver(state, newIsDeliver: boolean): void {
    state.isDeliver = newIsDeliver;
  },
  updateRestaurantStateMember(state, { stateMember, i, field, value }): void {
    i !== null ? (state.restaurant[stateMember][i][field] = value) : (state.restaurant[stateMember][field] = value);
  },
  addToBasket(state, orderMenu: IOrderMenu): void {
    let hash = '0';
    hash += orderMenu.menuId;
    hash += orderMenu.variantId;
    orderMenu.addons.forEach(orderAddon => (hash += orderAddon.addonId));
    orderMenu.addons.forEach(orderAddon => (hash += orderAddon.quantity));
    orderMenu.hash = hash;
    if (!state.orderDetail.orderMenus) state.orderDetail.orderMenus = [];
    const found = state.orderDetail.orderMenus.find(orderMenu => orderMenu.hash === hash);
    found ? (found.quantity += orderMenu.quantity) : state.orderDetail.orderMenus.push(orderMenu);
  },
  deleteOrderMenu(state, i: number): void {
    state.orderDetail.orderMenus.splice(i, 1);
  },
  setAddressForDelivery(state, address: IAddress) {
    state.orderDetail.deliverAddress = state.orderDetail.deliverAddress ?? new Address();
    state.orderDetail.deliverAddress.city = address.city;
    state.orderDetail.deliverAddress.street = address.street;
    state.orderDetail.deliverAddress.zipcode = address.zipcode;
    state.orderDetail.deliverAddress.number = address.number;
  },
  setOrderDetail(state, newOrderDetail: IOrderDetail) {
    state.orderDetail = newOrderDetail;
  },
  resetBasket(state) {
    state.orderDetail.orderMenus = [];
  },
};
